import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { 
  Car, Clock, Wifi, Shield, Star, MapPin, Phone, CheckCircle, ArrowRight,
  Briefcase, Plane, Timer, Users, Calendar, Laptop, Coffee
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import audiDowntown from "@/assets/audi-downtown.png";

const SanAntonioToHouston = () => {
  // Pricing calculation using Rick's formula
  const baseRate = 595; // Houston flat rate
  const gratuity = baseRate * 0.20;
  const total = baseRate + gratuity;
  const distance = 200; // miles
  const duration = 180; // minutes (3 hours)

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "San Antonio to Houston Private Car Service",
    "description": "Executive transport from San Antonio to Houston. Door-to-door luxury car service in a 2025 Audi Q7 with WiFi. Perfect for business travelers, medical center visits, and corporate meetings.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "email": "ricksblackcarservice@outlook.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "areaServed": ["San Antonio", "Houston", "Texas"],
    "serviceType": "Executive Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does it take to drive from San Antonio to Houston?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The drive from San Antonio to Houston typically takes 2.5-3 hours via I-10, depending on traffic conditions. Our chauffeurs monitor traffic in real-time and adjust routes as needed."
        }
      },
      {
        "@type": "Question",
        "name": "Is driving better than flying from San Antonio to Houston?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For many travelers, yes. When you factor in airport arrival time, TSA security, boarding, flight time, and ground transportation on both ends, driving is often comparable in total time. Plus, you can work productively in our WiFi-equipped Audi Q7 during the entire trip."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide transportation to Houston Medical Center?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. We provide premium transportation from San Antonio to the Texas Medical Center, MD Anderson Cancer Center, Houston Methodist, and all medical facilities in Houston. We understand the importance of comfortable, stress-free travel for medical appointments."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>San Antonio to Houston Private Car Service | Executive Transport | Rick's Black Car</title>
        <meta 
          name="description" 
          content="Executive transport San Antonio to Houston. Work from our WiFi-equipped Audi Q7 while we handle I-10. Door-to-door in ~3 hours. Medical Center & corporate transportation." 
        />
        <meta name="keywords" content="Executive transport San Antonio to Houston, Private car service Houston, Texas Medical Center transportation, San Antonio Houston car service, business travel Houston, Houston airport transfer" />
        <link rel="canonical" href="https://www.ricksblackcarservicesantonio.com/san-antonio-to-houston" />
        <meta property="og:title" content="San Antonio to Houston Private Car Service | Executive Transport" />
        <meta property="og:description" content="Work productively while we handle I-10. Door-to-door luxury service in ~3 hours. Perfect for business travelers." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[75vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${audiDowntown})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />
          
          <div className="relative z-10 container mx-auto px-4 py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-6">
              <Briefcase className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Executive Transport</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              San Antonio to Houston<br />
              <span className="text-primary">Private Car Service</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 max-w-3xl mx-auto">
              Work securely from the back of a 2025 Audi Q7 with Wi-Fi<br />
              while we handle the I-10 corridor.
            </p>
            
            <p className="text-lg text-primary font-semibold mb-8">
              Door-to-door in ~3 hours • No TSA • No parking hassles
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <Link to="/book">
                  Book Houston Transfer — ${total}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10">
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  (210) 296-7409
                </a>
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-3 max-w-3xl mx-auto">
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-primary">{distance}</div>
                <div className="text-xs md:text-sm text-gray-300">Miles</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-primary">~3</div>
                <div className="text-xs md:text-sm text-gray-300">Hours</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-primary">${total}</div>
                <div className="text-xs md:text-sm text-gray-300">All-In</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <Wifi className="w-6 h-6 md:w-8 md:h-8 text-primary mx-auto" />
                <div className="text-xs md:text-sm text-gray-300">WiFi</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Drive vs Fly Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Smart Executives Choose to Drive
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                When you factor in the full travel experience, driving is often more efficient.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Flying Column */}
                <Card className="bg-red-500/5 border-red-500/20">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                        <Plane className="w-6 h-6 text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">Flying SAT → IAH/HOU</h3>
                        <p className="text-sm text-red-400">The Hidden Time Costs</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Arrive at SAT early</span>
                        <span className="text-foreground font-medium">+60 min</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">TSA security line</span>
                        <span className="text-foreground font-medium">+20 min</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Boarding & taxi</span>
                        <span className="text-foreground font-medium">+25 min</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Flight time</span>
                        <span className="text-foreground font-medium">~55 min</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Deplane & baggage</span>
                        <span className="text-foreground font-medium">+25 min</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Ground transport in Houston</span>
                        <span className="text-foreground font-medium">+45 min</span>
                      </div>
                      <div className="flex items-center justify-between py-3 bg-red-500/10 rounded-lg px-3 mt-4">
                        <span className="text-foreground font-bold">Total Door-to-Door</span>
                        <span className="text-red-400 font-bold text-xl">~4+ hours</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Driving Column */}
                <Card className="bg-primary/5 border-primary/30">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <Car className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">Rick's Black Car Service</h3>
                        <p className="text-sm text-primary">The Productive Choice</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">We pick you up</span>
                        <span className="text-primary font-medium">Door pickup</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Security screening</span>
                        <span className="text-primary font-medium">None</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Work during travel</span>
                        <span className="text-primary font-medium">3 hours productive</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">WiFi & quiet cabin</span>
                        <span className="text-primary font-medium">Included</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">We drop you off</span>
                        <span className="text-primary font-medium">Your destination</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Luggage limits</span>
                        <span className="text-primary font-medium">None</span>
                      </div>
                      <div className="flex items-center justify-between py-3 bg-primary/10 rounded-lg px-3 mt-4">
                        <span className="text-foreground font-bold">Total Door-to-Door</span>
                        <span className="text-primary font-bold text-xl">~3 hours</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg text-muted-foreground">
                  <span className="text-primary font-semibold">Save 1+ hour</span> and arrive refreshed, prepared, and productive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Productivity Features */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Your Mobile Office on I-10
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                The 2025 Audi Q7 Black Optic is designed for executive productivity.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wifi className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">In-Vehicle WiFi</h3>
                  <p className="text-sm text-muted-foreground">
                    Secure, high-speed connection for video calls, emails, and document work.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Laptop className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">USB-C Charging</h3>
                  <p className="text-sm text-muted-foreground">
                    Keep all devices charged. Multiple ports for laptops, phones, and tablets.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Quiet Cabin</h3>
                  <p className="text-sm text-muted-foreground">
                    Premium sound insulation. Take confidential calls without background noise.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Coffee className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Complimentary Water</h3>
                  <p className="text-sm text-muted-foreground">
                    Bottled water provided. Climate-controlled comfort throughout your journey.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Transparent Pricing — No Surprises
                </h2>
                <p className="text-lg text-muted-foreground">
                  All prices include 20% gratuity. What you see is what you pay.
                </p>
              </div>

              <Card className="bg-card border-2 border-primary/30 overflow-hidden">
                <div className="bg-primary/10 px-6 py-4 border-b border-primary/20">
                  <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    San Antonio ↔ Houston (One Way)
                  </h3>
                </div>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-foreground mb-4">Route Details</h4>
                      <ul className="space-y-3 text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Distance: ~{distance} miles via I-10
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Duration: ~2.5-3 hours
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Vehicle: 2025 Audi Q7 Black Optic
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          WiFi & USB charging included
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Up to 6 passengers
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-6">
                      <h4 className="font-semibold text-foreground mb-4">Pricing Breakdown</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Houston Flat Rate:</span>
                          <span className="text-foreground font-medium">${baseRate.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Gratuity (20%):</span>
                          <span className="text-foreground font-medium">${gratuity.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-border pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="text-foreground font-bold">Total:</span>
                            <span className="text-primary font-bold text-xl">${total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        Late night surcharge ($35) applies for pickups 10 PM - 5 AM.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                    <Button asChild size="lg" className="text-lg">
                      <Link to="/book">
                        Book Houston Transfer
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                      <Link to="/quick-quote">Get Round-Trip Quote</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Popular Houston Destinations */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Popular Houston Destinations We Serve
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From world-class medical centers to corporate headquarters, we get you there in style.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="bg-card border-border hover:border-primary/50 transition-colors group">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🏥</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">Medical Center</h3>
                  <p className="text-sm text-muted-foreground">
                    Texas Medical Center, MD Anderson, Houston Methodist — comfortable, stress-free transport.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors group">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🏢</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">Downtown Houston</h3>
                  <p className="text-sm text-muted-foreground">
                    Corporate meetings, conferences, and business events in the heart of the city.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors group">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">✈️</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">IAH & Hobby</h3>
                  <p className="text-sm text-muted-foreground">
                    George Bush Intercontinental or Hobby Airport — we drop you curbside.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors group">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🛢️</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">Energy Corridor</h3>
                  <p className="text-sm text-muted-foreground">
                    Oil & gas headquarters, ExxonMobil, Chevron, and more along I-10 West.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Houston Destinations List */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Houston Destinations We Serve
              </h2>
              <p className="text-muted-foreground">
                Door-to-door service anywhere in the Houston metro area
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {[
                "George Bush Intercontinental (IAH)",
                "Hobby Airport (HOU)",
                "Texas Medical Center",
                "MD Anderson Cancer Center",
                "Downtown Houston",
                "The Galleria",
                "Energy Corridor",
                "Memorial City",
                "The Woodlands",
                "Sugar Land",
                "Minute Maid Park",
                "NRG Stadium"
              ].map((area) => (
                <span 
                  key={area}
                  className="bg-muted px-4 py-2 rounded-full text-sm text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary/10 border-y border-primary/20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Travel San Antonio to Houston?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Skip the airport hassle. Work productively during your trip. Arrive refreshed and on time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/book">Book Now — ${total.toFixed(2)}</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  Call (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default SanAntonioToHouston;
